import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { repository } from "@/infrastructure/repositories";
import { useAuth } from "@/components/AuthProvider";
import type { Condition } from "@/domain/repositoryInterface/Repository";
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";

export type ShopRow = Tables<"shops">;
export type AreaRow = Tables<"areas">;

const areaRepo = repository("areas");
const shopRepo = repository("shops");
const brandRepo = repository("brands");

export function useAreas() {
  return useQuery<AreaRow[]>({
    queryKey: ["areas"],
    queryFn: async () => {
      const { data } = await areaRepo.findMany({
        where: [{ column: "deleted_at", op: "isNull" }],
        orderBy: [{ column: "sort_order" }],
      });
      return data;
    },
  });
}

export function useShops() {
  const { authUser } = useAuth();
  return useQuery<ShopRow[]>({
    queryKey: ["shops", authUser?.role, authUser?.shop_id],
    queryFn: async () => {
      const where: Condition[] = [{ column: "deleted_at", op: "isNull" }];
      if (authUser?.role === "shop" && authUser.shop_id) {
        where.push({ column: "id", op: "eq", value: authUser.shop_id });
      }
      // company_id filter removed (T-13): RLS scopes shops via brand → company
      const { data } = await shopRepo.findMany({
        where,
        orderBy: [{ column: "sort_order" }],
      });
      return data;
    },
  });
}

export interface ShopPayload {
  shop_name: string;
  brand_id?: string | null;
  area_id?: string | null;
  slot?: number | null;
  scale?: number | null;
  email1?: string;
  email2?: string;
  phone1?: string;
  phone2?: string;
  zip_code?: string;
  shop_address?: string;
  shop_url?: string;
  line_url?: string;
  description?: string;
  is_public?: boolean;
  user_edit_type?: number | null;
  sort_order?: number | null;
}

function buildRecord(v: ShopPayload) {
  const rec: Record<string, unknown> = {
    shop_name: v.shop_name,
    area_id: v.area_id || null,
    slot: v.slot ?? null,
    scale: v.scale ?? null,
    email1: v.email1 || null,
    email2: v.email2 || null,
    phone1: v.phone1 || null,
    phone2: v.phone2 || null,
    zip_code: v.zip_code || null,
    shop_address: v.shop_address || null,
    shop_url: v.shop_url || null,
    line_url: v.line_url || null,
    description: v.description || null,
  };
  if (v.is_public !== undefined) rec.is_public = v.is_public;
  if (v.user_edit_type !== undefined) rec.user_edit_type = v.user_edit_type;
  if (v.sort_order !== undefined) rec.sort_order = v.sort_order;
  return rec;
}

async function resolveBrandId(explicit: string | null | undefined, companyId: string | null | undefined): Promise<string> {
  if (explicit) return explicit;
  const where: Condition[] = [{ column: "deleted_at", op: "isNull" }];
  if (companyId) where.push({ column: "company_id", op: "eq", value: companyId });
  const { data } = await brandRepo.findMany<{ id: string }>({
    select: "id",
    where,
    orderBy: [{ column: "sort_order" }],
    page: { index: 0, size: 1 },
  });
  if (!data || data.length === 0) {
    throw new Error("店舗の所属ブランドが見つかりません。先に企業・ブランドを作成してください。");
  }
  return data[0].id;
}

export function useCreateShop() {
  const qc = useQueryClient();
  const { authUser } = useAuth();
  return useMutation({
    mutationFn: async (v: ShopPayload) => {
      const rec = buildRecord(v) as Record<string, unknown>;
      rec.brand_id = await resolveBrandId(v.brand_id, authUser?.company_id ?? null);
      await shopRepo.create(rec as unknown as TablesInsert<"shops">);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shops"] }),
  });
}

export function useUpdateShop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...v }: ShopPayload & { id: string }) => {
      await shopRepo.update({ id, patch: buildRecord(v) as TablesUpdate<"shops"> });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shops"] }),
  });
}

export function useSoftDeleteShop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await shopRepo.update({ id, patch: { deleted_at: new Date().toISOString() } });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shops"] }),
  });
}

// ===== Save all shops inline =====
export function useSaveShopsInline() {
  const qc = useQueryClient();
  const { authUser } = useAuth();

  return useMutation({
    mutationFn: async (
      rows: Array<ShopPayload & { id: string; markedForDelete?: boolean; isNew?: boolean }>
    ) => {
      for (const row of rows) {
        if (row.markedForDelete && !row.isNew) {
          await shopRepo.update({ id: row.id, patch: { deleted_at: new Date().toISOString() } });
        } else if (!row.markedForDelete) {
          if (row.isNew) {
            const rec = buildRecord(row) as Record<string, unknown>;
            rec.brand_id = await resolveBrandId(row.brand_id, authUser?.company_id ?? null);
            await shopRepo.create(rec as unknown as TablesInsert<"shops">);
          } else {
            await shopRepo.update({ id: row.id, patch: buildRecord(row) as TablesUpdate<"shops"> });
          }
        }
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shops"] }),
  });
}

// ===== Shop Hours =====
// Single-shop version re-exported from useShifts.ts
export { useShopHours } from "@/hooks/useShifts";
export type { ShopHoursRow as ShopHourRow } from "@/hooks/useShifts";

// All shop hours (for ShopsPage multi-shop view)
export function useAllShopHours() {
  return useQuery<Tables<"shop_hours">[]>({
    queryKey: ["shop_hours_all"],
    queryFn: async () => {
      const { data } = await repository("shop_hours").findMany({
        where: [{ column: "deleted_at", op: "isNull" }],
        orderBy: [{ column: "sort_order" }],
      });
      return data;
    },
  });
}

// ===== Save Shop Hours (upsert) =====
// upsert (onConflict) は Repository 契約外のため supabase 直叩きのまま残す。
export function useSaveShopHours() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      shopId,
      days,
    }: {
      shopId: string;
      days: Array<{
        day_of_week: string;
        is_holiday: boolean;
        open_time: string;
        close_time: string;
        sort_order: number;
      }>;
    }) => {
      for (const day of days) {
        const { error } = await supabase
          .from("shop_hours")
          .upsert(
            {
              shop_id: shopId,
              day_of_week: day.day_of_week as any,
              is_holiday: day.is_holiday,
              open_time: day.is_holiday ? null : day.open_time,
              close_time: day.is_holiday ? null : day.close_time,
              sort_order: day.sort_order,
            },
            { onConflict: "shop_id,day_of_week" }
          );
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shop_hours"] });
      qc.invalidateQueries({ queryKey: ["shop_hours_all"] });
    },
  });
}
